// Utility helpers
import { todayIST, tomorrowIST } from "./istDate";
import { lineWithFlat } from "./customerUnit";

export const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
export const formatINRPrecise = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
export const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
export const formatDateShort = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};
export const formatTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};
/** A tenant-configured "HH:MM" wall-clock (24h) as 12-hour text.
 *  "06:00" → "6:00 AM", "17:30" → "5:30 PM". Distinct from formatTime()
 *  above, which formats an ISO datetime — these are the bare clock strings
 *  stored on Tenant.delivery_windows. */
export const formatClock = (hhmm) => {
  if (!hhmm || !String(hhmm).includes(":")) return hhmm || "";
  const [hStr, mStr] = String(hhmm).split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm || "";
  const period = h >= 12 ? "PM" : "AM";
  const h12 = (h % 12) || 12;
  return m === 0 ? `${h12}:00 ${period}` : `${h12}:${mStr.padStart(2, "0")} ${period}`;
};

/** A delivery window as a range: "6:00 AM – 10:00 AM".
 *
 *  Every customer-facing mention of when a delivery arrives must show BOTH
 *  ends of the admin-configured window. Showing `slot_end` alone read as a
 *  promise of one arrival time ("at 10:00 AM") when 10:00 is merely the
 *  close of a 6:00–10:00 window.
 *
 *  Degrades to whichever end exists so a half-configured window still says
 *  something truthful instead of "undefined – undefined". */
export const formatSlotWindow = (w) => {
  const start = formatClock(w?.slot_start);
  const end = formatClock(w?.slot_end);
  if (start && end) return `${start} – ${end}`;
  return start || end || "";
};

// IST calendar dates (see lib/istDate.js) — the UTC date these used to return
// still read as yesterday between 00:00 and 05:30 IST.
export const todayISO = () => todayIST();
export const tomorrowISO = () => tomorrowIST();
/** "Full Cream Milk" → "FCM" — used as an image placeholder when a product
 *  (or anything else named) has no photo. Caps at 3 letters so long names
 *  stay legible inside a small circular badge. Words are stripped of
 *  leading punctuation first — a name like "Cow Milk (A2)" would otherwise
 *  take "(" as the third word's "initial", showing "CM(" in the badge. */
export const getInitials = (name, max = 3) =>
  String(name || "")
    .split(/\s+/)
    .map((w) => w.replace(/^[^a-zA-Z0-9]+/, ""))
    .filter(Boolean)
    .slice(0, max)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

/** Full, human-readable delivery address from an Address object
 *  ({line1, line2, city, pincode, landmark} — see backend/models.py).
 *  Skips whatever's missing so partially-filled addresses never render
 *  stray ", ," separators, and appends the landmark as a "near X" hint.
 *  Riders navigate by this, so it deliberately includes city + landmark
 *  rather than the line1+pincode fragment that used to be shown. */
export const formatAddress = (addr) => {
  if (!addr) return "";
  // A typed address's own flat / house number goes in front of line1
  // (apartment addresses already start with it) — lib/customerUnit.
  const parts = [lineWithFlat(addr), addr.line2, addr.city, addr.pincode]
    .map((p) => String(p || "").trim())
    .filter(Boolean);
  const base = parts.join(", ");
  const landmark = String(addr.landmark || "").trim();
  return landmark ? `${base}${base ? " · " : ""}near ${landmark}` : base;
};

export const formatRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};
